import { useState, useMemo, useRef } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Header } from "@/components/Header";
import { ScholarGate, isMemberUser } from "@/components/ScholarGate";
import { apiError } from "@/lib/api";
import { faqService } from "@/services/faqService";
import { useFaqList, useAskFaq, usePinFaq, useDeleteFaq } from "@/hooks/useFaq";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MessageCircleQuestion, Sparkles, Pin, PinOff, Trash2, Search, Layers, List, ChevronDown, ChevronRight, GraduationCap, Volume2, Loader2 } from "lucide-react";

const GENERAL = "General questions";

export function FaqContent() {
  const { user } = useAuth();
  const [question, setQuestion] = useState("");
  const [search, setSearch] = useState("");
  const [byCourse, setByCourse] = useState(false);
  const [collapsed, setCollapsed] = useState({});
  const [narrating, setNarrating] = useState(null);
  const audioRef = useRef(null);

  const member = isMemberUser(user);
  const { data: entries = [], isLoading } = useFaqList(search);

  const ask = useAskFaq({
    onSuccess: () => { setQuestion(""); toast.success("Answer added to your FAQ"); },
    onError: (e) => toast.error(apiError(e)),
  });
  const pin = usePinFaq({ onError: (e) => toast.error(apiError(e)) });
  const del = useDeleteFaq({ onSuccess: () => toast.success("Removed"), onError: (e) => toast.error(apiError(e)) });

  const submitAsk = (e) => {
    e.preventDefault();
    if (!question.trim()) return;
    ask.mutate(question.trim());
  };

  const replay = async (id) => {
    setNarrating(id);
    try {
      const res = await faqService.narrate(id);
      if (res.audio && audioRef.current) {
        audioRef.current.src = `data:audio/mpeg;base64,${res.audio}`;
        audioRef.current.play().catch(() => {});
      }
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setNarrating(null);
    }
  };

  const groups = useMemo(() => {
    const map = new Map();
    entries.forEach((en) => {
      const key = en.source === "course_qa" && en.course_title ? en.course_title : GENERAL;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(en);
    });
    // Course groups first (alphabetical), General last.
    return Array.from(map.entries()).sort((a, b) => {
      if (a[0] === GENERAL) return 1;
      if (b[0] === GENERAL) return -1;
      return a[0].localeCompare(b[0]);
    });
  }, [entries]);

  const card = (en, i) => (
    <motion.div key={en.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}
      className={`border bg-card p-5 ${en.pinned ? "border-primary" : "border-border"}`} data-testid={`faq-item-${en.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="font-display font-bold leading-snug">{en.question}</h3>
          {!byCourse && en.source === "course_qa" && en.course_title && (
            <span className="inline-flex items-center gap-1 mt-1 text-[11px] text-primary font-semibold" data-testid={`faq-course-tag-${en.id}`}>
              <GraduationCap className="h-3 w-3" /> {en.course_title}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button onClick={() => replay(en.id)} disabled={narrating === en.id} className="p-1.5 text-muted-foreground hover:text-primary transition-colors disabled:opacity-50" data-testid={`faq-replay-${en.id}`} title="Replay narration">
            {narrating === en.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Volume2 className="h-4 w-4" />}
          </button>
          <button onClick={() => pin.mutate(en.id)} className={`p-1.5 hover:bg-secondary transition-colors ${en.pinned ? "text-primary" : "text-muted-foreground"}`} data-testid={`faq-pin-${en.id}`}>
            {en.pinned ? <Pin className="h-4 w-4" /> : <PinOff className="h-4 w-4" />}
          </button>
          <button onClick={() => del.mutate(en.id)} className="p-1.5 text-muted-foreground hover:text-destructive transition-colors" data-testid={`faq-delete-${en.id}`}>
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>
      <p className="text-sm text-muted-foreground mt-2 whitespace-pre-line leading-relaxed">{en.answer}</p>
    </motion.div>
  );

  return (
    <div className="max-w-3xl mx-auto px-6 md:px-8" data-testid="faq-content">
      <div className="flex items-center gap-2 mb-2">
          <MessageCircleQuestion className="h-5 w-5 text-primary" />
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-primary">Personal FAQ</p>
        </div>
        <h1 className="font-display text-4xl font-extrabold tracking-tighter mb-2">Ask. Learn. Pin.</h1>
        <p className="text-muted-foreground mb-8">Ask any question, keep a private Q&A feed, and pin the answers that matter.</p>

        {member ? (
          <form onSubmit={submitAsk} className="mb-6" data-testid="faq-ask-form">
            <div className="flex items-stretch border border-foreground bg-card">
              <div className="flex items-center pl-4 text-primary"><Sparkles className="h-5 w-5" /></div>
              <Input value={question} onChange={(e) => setQuestion(e.target.value)}
                placeholder="Ask anything..." className="border-0 rounded-none h-14 text-base focus-visible:ring-0 shadow-none" data-testid="faq-ask-input" />
              <Button type="submit" disabled={ask.isPending} className="rounded-none h-14 px-6 bg-primary hover:opacity-90" data-testid="faq-ask-button">
                {ask.isPending ? "Thinking..." : "Ask"}
              </Button>
            </div>
          </form>
        ) : (
          <div className="mb-6" data-testid="faq-ask-gate"><ScholarGate feature="Ask anything (general questions)" /></div>
        )}

        <audio ref={audioRef} />

        <div className="flex flex-wrap items-center gap-3 mb-6">
              <div className="flex items-stretch border border-border bg-card flex-1 min-w-[220px]">
                <div className="flex items-center pl-4 text-muted-foreground"><Search className="h-4 w-4" /></div>
                <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search your questions..."
                  className="border-0 rounded-none h-11 focus-visible:ring-0 shadow-none" data-testid="faq-search-input" />
              </div>
              <div className="inline-flex border border-border" data-testid="faq-view-toggle">
                <button onClick={() => setByCourse(false)} data-testid="faq-view-all"
                  className={`inline-flex items-center gap-1.5 px-3 h-11 text-sm font-semibold transition-colors ${!byCourse ? "bg-primary text-white" : "text-muted-foreground hover:text-foreground"}`}>
                  <List className="h-4 w-4" /> All
                </button>
                <button onClick={() => setByCourse(true)} data-testid="faq-view-by-course"
                  className={`inline-flex items-center gap-1.5 px-3 h-11 text-sm font-semibold transition-colors ${byCourse ? "bg-primary text-white" : "text-muted-foreground hover:text-foreground"}`}>
                  <Layers className="h-4 w-4" /> By course
                </button>
              </div>
            </div>

            {isLoading ? (
              <div className="py-10 flex justify-center"><div className="h-8 w-8 border-2 border-primary border-t-transparent animate-spin rounded-full" /></div>
            ) : entries.length === 0 ? (
              <div className="border border-dashed border-border bg-card p-12 text-center text-muted-foreground" data-testid="faq-empty">
                {search ? "No matching questions." : "No questions yet. Ask your first above — or finish a course and use the Q&A slide!"}
              </div>
            ) : byCourse ? (
              <div className="space-y-5" data-testid="faq-grouped">
                {groups.map(([title, items]) => {
                  const isOpen = !collapsed[title];
                  return (
                    <div key={title} className="border border-border bg-card" data-testid={`faq-group-${title}`}>
                      <button onClick={() => setCollapsed((c) => ({ ...c, [title]: isOpen }))}
                        className="w-full flex items-center justify-between gap-3 px-5 py-4 hover:bg-secondary/40 transition-colors"
                        data-testid={`faq-group-header`}>
                        <span className="flex items-center gap-2 font-display font-bold text-left">
                          {title === GENERAL ? <Sparkles className="h-4 w-4 text-primary" /> : <GraduationCap className="h-4 w-4 text-primary" />}
                          {title}
                          <span className="text-xs text-muted-foreground font-normal">({items.length})</span>
                        </span>
                        {isOpen ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
                      </button>
                      {isOpen && <div className="p-4 pt-0 space-y-3">{items.map((en, i) => card(en, i))}</div>}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="space-y-4" data-testid="faq-list">
                {entries.map((en, i) => card(en, i))}
              </div>
            )}
    </div>
  );
}

export default function FAQ() {
  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="py-12"><FaqContent /></main>
    </div>
  );
}
